import { useState } from "react"

import { BadgeMedallion } from "@/components/badges/badge-medallion"
import type { MedallionViewModel } from "@/lib/badge-medallions"
import { cn } from "@/lib/utils"

/**
 * OKS-969 — the reusable medallion strip: cap, overflow, empty state, layout.
 *
 * # It does NOT sort (BRIEF AC4)
 *
 * `items` arrive already ordered by the server (tier desc → newest-award-in-
 * family desc → family identifier asc) and are rendered in the order given.
 * The sort and the cap must not sit on opposite sides of the wire: a cap taken
 * over an unsorted list drops the wrong families, and a second sort here could
 * disagree with OKS-970's. `Badges::MedallionBuilder` is the one home for the
 * order.
 *
 * That every consumer shares one order is a property of THIS component doing
 * nothing — not of the ordering being unreachable from here. `TIER_CLASSES` in
 * `lib/badge-tiers.ts` is already declared bronze → silver → gold → special, so
 * a rank is one `Object.keys(...).indexOf` away (and would break silently the
 * first time that object is reordered for readability). Nothing stops a future
 * consumer sorting; what keeps them consistent is that they never need to.
 *
 * # It composes no copy (the OKS-937 rule)
 *
 * No `useTranslation` in this file. Every user-facing string arrives through
 * `labels`, already translated by the caller. A component that composes copy
 * without i18n produces untranslated strings *by construction*; making the
 * labels required props turns a forgotten string into a `tsc` failure. The
 * label *rules* still have exactly one home — `lib/badge-medallions.ts` — so
 * hermetic component and single-homed rule are not in tension: both consumers
 * write one-line adapters over the same helpers.
 *
 * # Reusability is a property of this prop list (BRIEF AC7)
 *
 * No Inertia import, no `@/routes` import, no `crew_membership` concept. The
 * host page's Card chrome lives in `<CreatorBadgesSection>` precisely so this
 * component drops into OKS-970's 64px roster cell without a Card to fight.
 */
export interface BadgeMedallionStripLabels {
  /** `aria-label` on the list. */
  strip: string
  /** Single muted line when there are no medallions (BRIEF D8). */
  empty: string
  /** One medallion's complete accessible name — brief or count-bearing (AC8). */
  medallion: (vm: MedallionViewModel) => string
  /** `×N` chip text. Only called when `vm.count > 1`. */
  countChip: (count: number) => string
  /** Popover content for one medallion. Not called when `interactive` is false. */
  popover: (vm: MedallionViewModel) => { title: string; lines: string[] }
  /**
   * OKS-975 — already-translated family name for the inert medallion's native
   * hover tooltip (AC9). Optional for the same reason as `hint`: supplying it
   * is the call site's decision. `<BadgeMedallion>` renders it on the inert
   * branch only, so an interactive strip that supplies one still gets no
   * tooltip stacked on its popover.
   */
  hoverName?: (vm: MedallionViewModel) => string
  /**
   * OKS-975 — muted standing line telling the reader the discs open
   * (BRIEF D2 / AC6). Already translated, like every other label here.
   *
   * ⚠️ The ONLY optional label in this object, and that is the design, not an
   * oversight. Everything else is required so a forgotten string is a `tsc`
   * failure rather than a runtime blank; this one's ABSENCE is a product
   * decision — the roster deliberately renders no hint (AC8) — so omitting it
   * must compile. Presence is the call site's choice; there is no flag to read
   * and nothing to drift.
   */
  hint?: string
  /** Visible `+N` control text. */
  more: (hidden: number) => string
  /** Accessible name for the `+N` control. */
  moreAria: (hidden: number) => string
  /** Collapse control text. */
  less: string
}

interface BadgeMedallionStripProps {
  /** Server-ordered; rendered as given. */
  items: MedallionViewModel[]
  /** `preview` 48px (default) | `corner` 28px (roster density). */
  size?: "preview" | "corner"
  /** How many render before the overflow control. This page passes 8. */
  max?: number
  /** When false the `+N` control is inert text — OKS-970's dense cell. */
  expandable?: boolean
  /** When false no popover triggers are rendered at all. */
  interactive?: boolean
  labels: BadgeMedallionStripLabels
  className?: string
}

export function BadgeMedallionStrip({
  items,
  size = "preview",
  max = 8,
  expandable = true,
  interactive = true,
  labels,
  className,
}: BadgeMedallionStripProps) {
  const [expanded, setExpanded] = useState(false)

  if (items.length === 0) {
    return (
      <p
        data-testid="badge-medallion-strip-empty"
        className="text-muted-foreground text-sm"
      >
        {labels.empty}
      </p>
    )
  }

  const overflowing = items.length > max
  const visible = overflowing && !expanded ? items.slice(0, max) : items
  const hidden = items.length - max

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <ul
        data-testid="badge-medallion-strip"
        data-size={size}
        data-total={items.length}
        aria-label={labels.strip}
        className={cn(
          "flex list-none flex-wrap items-center p-0",
          size === "corner" ? "gap-1.5" : "gap-3",
        )}
      >
        {visible.map((vm) => (
          <li key={vm.key} className="flex">
            <BadgeMedallion
              vm={vm}
              size={size}
              ariaLabel={labels.medallion(vm)}
              // No chip on a single-award family (BRIEF AC3): a "×1" would be
              // noise on every non-repeating badge, which is most of them.
              countChipLabel={
                vm.count > 1 ? labels.countChip(vm.count) : undefined
              }
              popover={interactive ? labels.popover(vm) : undefined}
              hoverName={labels.hoverName?.(vm)}
            />
          </li>
        ))}
      </ul>

      {overflowing &&
        (expandable ? (
          <button
            type="button"
            data-testid="badge-medallion-strip-more"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            aria-label={expanded ? labels.less : labels.moreAria(hidden)}
            className="text-muted-foreground hover:text-foreground w-fit text-xs underline-offset-2 hover:underline"
          >
            {expanded ? labels.less : labels.more(hidden)}
          </button>
        ) : (
          // Inert count for OKS-970's roster cell: the same overflow
          // information without an affordance, keeping `crew-roster-cell`'s
          // "the cell does not navigate" posture intact.
          <span
            data-testid="badge-medallion-strip-more"
            data-inert="true"
            className="text-muted-foreground w-fit text-xs"
          >
            {labels.more(hidden)}
          </span>
        ))}

      {/*
        OKS-975 — the resting-state affordance (BRIEF D2 / AC6). Rendered LAST,
        after the overflow control, so the `+N` keeps its adjacency to the discs
        and the hint absorbs the vertical growth instead of displacing it.

        Nothing renders it on the roster because that call site supplies no
        `hint` (AC8) — the absence of the label IS the absence of the line.

        An empty strip cannot reach here at all: the zero-items early return
        above fires first, so a creator with no badges is never told to select
        badges that aren't on screen. That is a property of control flow, not a
        condition someone could delete from this expression.
      */}
      {labels.hint && (
        <p
          data-testid="badge-medallion-strip-hint"
          className="text-muted-foreground text-xs"
        >
          {labels.hint}
        </p>
      )}
    </div>
  )
}
